// ADR-0014 §7.1 / §12 C-5 — the four steps of the tenant provisioning wizard and their states; since §7.5,
// how far step 2's invited administrator has got and what cancelling their invite did; and since §7.2 /
// §12 C-6, where the customer's own identity provider stands. As the UI names them.
//
// The source of truth for WHICH codes exist is the backend's `TenantProvisioningSteps`,
// `TenantProvisioningStepStates`, `TenantFirstAdminActivationStates`, `FirstAdminCancelOutcomes` and
// `FirstAdminInviteCompensations` (backend/src/Clms.Application/Admin/TenantProvisioningContracts.cs),
// plus `TenantFederationStates` (backend/src/Clms.Domain/Entities/TenantIdpFederation.cs). These
// lists must cover those sets exactly, and `src/i18n/tenant-provisioning-i18n.test.mjs` reads the C# file
// and both locale files and fails the build when they do not — because the failure mode is a wizard step
// rendered as a raw snake_case code, on the one surface whose whole job is to tell an operator what is left
// to do.

/** Every step the backend reports, in the order ADR-0014 §7.1 numbers them. The wizard renders this order. */
export const PROVISIONING_STEPS = [
  "tenant",
  "identity",
  "entitlements",
  "default_content",
] as const;

export type ProvisioningStepCode = (typeof PROVISIONING_STEPS)[number];

/** Every state a step can be in. */
export const PROVISIONING_STEP_STATES = ["pending", "not_available", "done"] as const;

export type ProvisioningStepState = (typeof PROVISIONING_STEP_STATES)[number];

/**
 * There is work the operator can do now and it has not been done — the value that makes a provisioning
 * incomplete. Named because three places branch on it: the step's badge, whether its form is offered, and
 * the wizard's own summary line.
 */
export const STEP_PENDING: ProvisioningStepState = "pending";

/** The step's implementation is a follow-up task, so there is nothing to retry. */
export const STEP_NOT_AVAILABLE: ProvisioningStepState = "not_available";

/** The step's work has been done. Named because the stepper draws a tick instead of a number for it. */
export const STEP_DONE: ProvisioningStepState = "done";

// THE FOLLOW-UP VOCABULARY IS GONE, and that is what "C-6 shipped" looks like on this side too.
//
// `PROVISIONING_FOLLOW_UPS` mirrored the backend's `TenantProvisioningFollowUps` — the ADR-0014 §12 tasks a
// step was still waiting on. It held `D-2` until step 4 was built and `C-6` until per-tenant federation
// was, and both are now built. The backend reports no follow-up on any step, so there is nothing here to
// name and nothing in either locale to label; `tenants.provisioning.followUp.*` went with it, found by the
// i18n guard exactly as that file's header said it would be.
//
// `TenantProvisioningStep.followUp` is still on the wire as a nullable field that nothing populates, for
// the next step whose work is somebody else's. The section renders nothing for it.

/** Is this code one the step vocabulary names (and can therefore be labelled)? */
export function hasStepLabel(code: string): code is ProvisioningStepCode {
  return (PROVISIONING_STEPS as readonly string[]).includes(code);
}

/** Is this one of the states the wizard knows how to render? */
export function hasStepStateLabel(state: string): state is ProvisioningStepState {
  return (PROVISIONING_STEP_STATES as readonly string[]).includes(state);
}

/**
 * The i18n key naming a step.
 *
 * An unknown code falls back to a generic label rather than rendering the raw value: this text is a
 * numbered heading in a wizard, and `default_content` sitting there reads as a broken page. The i18n guard
 * is what keeps the fallback unreachable.
 */
export function stepTitleKey(code: string): string {
  return hasStepLabel(code) ? `tenants.provisioning.step.${code}.title` : "tenants.provisioning.step.unknown";
}

/** The i18n key for a step's one-line explanation of what it does. */
export function stepBodyKey(code: string): string {
  return hasStepLabel(code) ? `tenants.provisioning.step.${code}.body` : "tenants.provisioning.step.unknown";
}

/** The i18n key for a state's badge text. */
export function stepStateKey(state: string): string {
  return hasStepStateLabel(state)
    ? `tenants.provisioning.state.${state}`
    : "tenants.provisioning.state.unknown";
}

// ── step 2 · the customer's own identity provider (ADR-0014 §7.2, §12 C-6) ────────────────────────

/**
 * Where a tenant's federation stands, in the order an arrangement moves through it — mirrors the backend's
 * `TenantFederationStates`.
 *
 * TWO OF THE FOUR ARE NOT THE OPERATOR'S TO ACT ON, and the labels have to say so. `not_configured` is a
 * legitimate permanent arrangement (the customer's people sign in with the platform realm's own
 * credentials, as every tenant provisioned before C-6 does), and `pending_apply` is the separately
 * credentialed provisioning component's turn, not theirs — the console's own request cannot write the
 * realm (§7.2), so "retry" would be advice about somebody else's machine. `failed` is the one that is
 * theirs: the issuer or the credential is wrong, and a corrected save is the remedy.
 */
export const FEDERATION_STATES = [
  "not_configured",
  "pending_apply",
  "applied",
  "failed",
] as const;

export type FederationState = (typeof FEDERATION_STATES)[number];

/** No customer identity provider has been asked for. Named because it decides which button is offered. */
export const FEDERATION_NOT_CONFIGURED: FederationState = "not_configured";

/** The component could not apply the arrangement — the one state that reopens provisioning step 2. */
export const FEDERATION_FAILED: FederationState = "failed";

/** Is this one of the federation states the console knows how to name? */
export function hasFederationLabel(state: string): state is FederationState {
  return (FEDERATION_STATES as readonly string[]).includes(state);
}

/** The i18n key for the federation badge's text. */
export function federationKey(state: string): string {
  return hasFederationLabel(state)
    ? `tenants.provisioning.federation.state.${state}`
    : "tenants.provisioning.federation.state.unknown";
}

/** The i18n key for the sentence under that badge, saying what the state means for the operator. */
export function federationBodyKey(state: string): string {
  return hasFederationLabel(state)
    ? `tenants.provisioning.federation.stateBody.${state}`
    : "tenants.provisioning.federation.stateBody.unknown";
}

// ── step 2 · how far the invited administrator has got (ADR-0014 §7.5.3) ───────────────────────────

/**
 * How far step 2's invited first administrator has got, in the order the person moves through it —
 * mirrors `TenantFirstAdminActivationStates`.
 *
 * NONE of them holds step 2 open: the step is `done` the moment the account is bound, which is what lets
 * steps 3 and 4 be finished and the tenant read as provisioned while the acceptance is outstanding (§7.5.3,
 * and what the product owner asked for). So this is a detail BESIDE the step's state, never a substitute
 * for it.
 */
export const FIRST_ADMIN_ACTIVATION_STATES = [
  "awaiting_activation",
  "awaiting_sign_in",
  "signed_in",
] as const;

export type FirstAdminActivation = (typeof FIRST_ADMIN_ACTIVATION_STATES)[number];

/**
 * Bound, mailed, and not finished with the activation link. Named because it is the ONE activation state
 * the console reports outside step 2 as well — the section badge and the page's nav both say so rather than
 * calling a provisioning whose administrator has not accepted simply "done".
 */
export const ACTIVATION_AWAITING_ACTIVATION: FirstAdminActivation = "awaiting_activation";

/** Is this one of the activation states the console knows how to name? */
export function hasActivationLabel(state: string): state is FirstAdminActivation {
  return (FIRST_ADMIN_ACTIVATION_STATES as readonly string[]).includes(state);
}

/** The i18n key for the activation badge's text — "Kutsuttu — odottaa aktivointia" and its two siblings. */
export function activationKey(state: string): string {
  return hasActivationLabel(state)
    ? `tenants.provisioning.identity.activation.${state}`
    : "tenants.provisioning.identity.activation.unknown";
}

/** The i18n key for the sentence under that badge, saying what the operator can do about it. */
export function activationBodyKey(state: string): string {
  return hasActivationLabel(state)
    ? `tenants.provisioning.identity.activationBody.${state}`
    : "tenants.provisioning.identity.activationBody.unknown";
}

/**
 * What cancelling an invite did to the realm ACCOUNT — mirrors `FirstAdminCancelOutcomes`.
 *
 * Two outcomes and not one, because the difference is the one the operator has to be told: an account this
 * console created and has now removed is gone from the realm, while one it merely unbound is still there
 * for whoever made it. Only the server knows which — the delete needs all three of §7.5.4's conditions,
 * and the credential probe behind the third is its alone.
 */
export const FIRST_ADMIN_CANCEL_OUTCOMES = ["deleted", "unbound"] as const;

export type FirstAdminCancelOutcome = (typeof FIRST_ADMIN_CANCEL_OUTCOMES)[number];

/** Is this one of the cancel outcomes the console can report? */
export function hasCancelOutcomeLabel(outcome: string): outcome is FirstAdminCancelOutcome {
  return (FIRST_ADMIN_CANCEL_OUTCOMES as readonly string[]).includes(outcome);
}

/** The i18n key for the cancel's success note, by what became of the account. */
export function cancelOutcomeKey(outcome: string): string {
  return hasCancelOutcomeLabel(outcome)
    ? `tenants.provisioning.identity.cancelled.${outcome}`
    : "tenants.provisioning.identity.cancelled.unknown";
}

/**
 * What a FAILED step 2 did about its own half-finished writes — mirrors `FirstAdminInviteCompensations`,
 * and arrives as the `compensation` member of the 502's problem details (task 6f332099).
 *
 * Nine endings behind one status, and the remedy differs in every one: retry and nothing else, go to the
 * Keycloak console and take a role off, delete an account by hand, or do nothing because the realm turned
 * out to hold the binding the step could not confirm. Only the server saw which — the compensation runs
 * against the realm — and until this vocabulary existed it said so in an English `detail` sentence the
 * console printed verbatim on a Finnish page.
 */
export const FIRST_ADMIN_INVITE_COMPENSATIONS = [
  "nothing_written",
  "already_bound",
  "binding_landed",
  "role_revoked",
  "revoke_failed",
  "realm_unreadable",
  "account_gone",
  "account_deleted",
  "account_delete_failed",
] as const;

export type FirstAdminInviteCompensation = (typeof FIRST_ADMIN_INVITE_COMPENSATIONS)[number];

/** Is this one of the compensation outcomes the console can state in its own words? */
export function hasCompensationLabel(outcome: string): outcome is FirstAdminInviteCompensation {
  return (FIRST_ADMIN_INVITE_COMPENSATIONS as readonly string[]).includes(outcome);
}

/** The i18n key for the remedy sentence that goes with a compensation outcome. */
export function compensationKey(outcome: string): string {
  return hasCompensationLabel(outcome)
    ? `tenants.provisioning.identity.compensation.${outcome}`
    : "tenants.provisioning.identity.compensation.unknown";
}
